import type { World } from './types.js';
import { ROAD_SPECS } from './osm/classify.js';
import { pavementReach } from './osm/pavements.js';
import { bboxOfPoly, distToSegment2, pointInPoly } from './geom.js';

/**
 * Spatial indices over the generated world, used by the car physics.
 *
 * Both are uniform grids rather than a tree: a race region is a couple of square
 * kilometres of fairly evenly distributed geometry, which is the case a flat grid is
 * best at, and a grid rebuilds in a handful of milliseconds so the client can throw it
 * away and remake it whenever the host picks a new region.
 *
 * These live in `shared/` because the tests exercise them in Node without a browser.
 */

const CELL = 24;

function cellKey(cx: number, cz: number): number {
  // 16-bit signed cell coordinates packed into one number — a region would have to be
  // 780 km across to overflow.
  return ((cx + 32768) << 16) | (cz + 32768);
}

// ---------------------------------------------------------------------------
// Drivable surface
// ---------------------------------------------------------------------------

interface RoadSegment {
  ax: number;
  az: number;
  bx: number;
  bz: number;
  /** Squared half-width, so the hot loop compares squared distances. */
  halfWidth2: number;
  /**
   * Squared distance out to the back of the pavement, on side 0 (left of travel) and side 1.
   * Zero where there is no kerb. Per side, because a mapped sidewalk widens one side only.
   */
  vergeLeft2: number;
  vergeRight2: number;
  speed: number;
}

interface JunctionDisc {
  x: number;
  z: number;
  r2: number;
  speed: number;
}

export interface SurfaceSample {
  /** Top-speed multiplier at this point; `offRoadSpeed` when nothing covers it. */
  speed: number;
  /**
   * On the carriageway proper. Not the same question as "does this cost me anything" — a
   * pavement is `false` here and still nothing like a field — and it stays the narrow
   * question because the tree placer, the pavement builder and the AI's lost-detection all
   * ask it meaning exactly that.
   */
  onRoad: boolean;
  /**
   * How far from tarmac this is, 0 to 1. Scales the grip and drag penalties in `stepCar`.
   *
   * The middle value is the point. A kerb and the paved strip behind it used to be the same
   * surface as an open field, which is how a car that clipped a corner ended up doing
   * 23 km/h on a paving slab — a penalty out of all proportion to the mistake, and the one
   * thing that made the handling feel punitive rather than difficult.
   */
  roughness: number;
  /**
   * On a pavement — up on the kerb, not on open ground. What `stepCar` watches for the moment
   * a car mounts or leaves it (DECISIONS.md §93).
   */
  pavement: boolean;
  /**
   * Unit vector from the centreline of the street under or beside this point out towards it,
   * so a car crossing a kerb knows how hard it went at it. Zero where nothing is near.
   */
  nx: number;
  nz: number;
}

/** Roughness of the paved strip behind the kerb: a real cost, nowhere near a field's. */
const VERGE_ROUGHNESS = 0.4;
/** Top-speed multiplier on that strip, against `offRoadSpeed` for open ground. */
const VERGE_SPEED = 0.74;

export class SurfaceIndex {
  private readonly grid = new Map<number, number[]>();
  private readonly segments: RoadSegment[] = [];
  private readonly discs: JunctionDisc[] = [];
  private readonly discGrid = new Map<number, number[]>();

  /** How fast you can go with all four wheels on grass. */
  readonly offRoadSpeed: number;

  constructor(world: World, offRoadSpeed = 0.58) {
    this.offRoadSpeed = offRoadSpeed;

    for (const road of world.roads) {
      const spec = ROAD_SPECS[road.cls];
      if (!spec.drivable) continue;
      // A touch narrower than the visible ribbon so that clipping a kerb actually costs
      // you speed — the visual edge and the grip edge lining up exactly feels mushy.
      const half = road.width * 0.46;
      const half2 = half * half;
      // Out to the back of the pavement that `worldMesh` draws beside this road, from the
      // same function that draws it. Roads too narrow for a kerb get no verge at all, which
      // is right: an alley has grass or a wall against it, not a paving slab.
      const [left, right] = pavementReach(road);
      const n = road.pts.length / 2;
      for (let i = 1; i < n; i++) {
        const index = this.segments.length;
        this.segments.push({
          ax: road.pts[(i - 1) * 2],
          az: road.pts[(i - 1) * 2 + 1],
          bx: road.pts[i * 2],
          bz: road.pts[i * 2 + 1],
          halfWidth2: half2,
          vergeLeft2: left * left,
          vergeRight2: right * right,
          speed: spec.speed,
        });
        insertSegment(
          this.grid,
          index,
          road.pts[(i - 1) * 2],
          road.pts[(i - 1) * 2 + 1],
          road.pts[i * 2],
          road.pts[i * 2 + 1],
          Math.max(half, left, right),
        );
      }
    }

    for (const j of world.junctions) {
      const index = this.discs.length;
      this.discs.push({ x: j.x, z: j.z, r2: j.r * j.r, speed: 1 });
      insertCircle(this.discGrid, index, j.x, j.z, j.r);
    }
  }

  get segmentCount(): number {
    return this.segments.length;
  }

  /** The best surface under a point. Junction discs always count as full-speed tarmac. */
  sample(x: number, z: number): SurfaceSample {
    const key = cellKey(Math.floor(x / CELL), Math.floor(z / CELL));

    const discs = this.discGrid.get(key);
    if (discs) {
      for (const di of discs) {
        const d = this.discs[di];
        if ((x - d.x) ** 2 + (z - d.z) ** 2 <= d.r2) {
          return { speed: d.speed, onRoad: true, roughness: 0, pavement: false, nx: 0, nz: 0 };
        }
      }
    }

    const bucket = this.grid.get(key);
    if (!bucket) return this.offRoad();

    // Carriageway wins outright wherever it is found; the verge is only consulted for a
    // point no road actually covers, so a pavement overlapping the next street along can
    // never make that street slower than it is.
    let best = 0;
    let bestD2 = Infinity;
    let bestX = 0;
    let bestZ = 0;
    let verge = false;
    let vergeD2 = Infinity;
    let vergeX = 0;
    let vergeZ = 0;
    for (const si of bucket) {
      const s = this.segments[si];
      const hit = distToSegment2(x, z, s.ax, s.az, s.bx, s.bz);
      if (hit.d2 <= s.halfWidth2) {
        if (s.speed > best || (s.speed === best && hit.d2 < bestD2)) {
          best = s.speed;
          bestD2 = hit.d2;
          bestX = hit.x;
          bestZ = hit.z;
        }
      } else if (best === 0 && hit.d2 < vergeD2) {
        const left = (s.bx - s.ax) * (z - s.az) - (s.bz - s.az) * (x - s.ax) < 0;
        if (hit.d2 <= (left ? s.vergeLeft2 : s.vergeRight2)) {
          verge = true;
          vergeD2 = hit.d2;
          vergeX = hit.x;
          vergeZ = hit.z;
        }
      }
    }
    if (best > 0) return { speed: best, onRoad: true, roughness: 0, pavement: false, ...outward(x, z, bestX, bestZ, bestD2) };
    if (verge) {
      // The pavement beside a fast road is no better than the pavement beside a slow one —
      // it is a paving slab either way — so the road's own class does not carry over.
      return {
        speed: VERGE_SPEED,
        onRoad: false,
        roughness: VERGE_ROUGHNESS,
        pavement: true,
        ...outward(x, z, vergeX, vergeZ, vergeD2),
      };
    }
    return this.offRoad();
  }

  private offRoad(): SurfaceSample {
    return { speed: this.offRoadSpeed, onRoad: false, roughness: 1, pavement: false, nx: 0, nz: 0 };
  }
}

function outward(x: number, z: number, cx: number, cz: number, d2: number): { nx: number; nz: number } {
  if (d2 < 1e-8) return { nx: 0, nz: 0 };
  const d = Math.sqrt(d2);
  return { nx: (x - cx) / d, nz: (z - cz) / d };
}

// ---------------------------------------------------------------------------
// Buildings
// ---------------------------------------------------------------------------

interface Wall {
  ax: number;
  az: number;
  bx: number;
  bz: number;
  /** Outward unit normal — lets us push a car back out even from inside a footprint. */
  nx: number;
  nz: number;
  /** Index into `rings`, so a hit knows which footprint it belongs to. */
  ring: number;
}

export interface Hit {
  /** Push-out vector to apply to the car's centre. */
  px: number;
  pz: number;
  /** Unit surface normal of whatever was hit. */
  nx: number;
  nz: number;
  depth: number;
}

export class BuildingIndex {
  private readonly grid = new Map<number, number[]>();
  private readonly walls: Wall[] = [];
  /**
   * Outer rings, plus their walls and a bbox grid over them.
   *
   * Containment has to be answered against the whole ring, not wall by wall: a point just
   * outside one side of a rectangle is behind the *plane* of the three other sides, so a
   * per-wall inside test reports a car driving past a building as being inside it.
   */
  private readonly rings: number[][] = [];
  private readonly ringWalls: number[][] = [];
  private readonly ringGrid = new Map<number, number[]>();

  constructor(world: World) {
    for (const b of world.buildings) {
      // Buildings a road legitimately runs through — an archway, a tunnel, a viaduct on
      // another layer — are drawn but not collided with. See `openPassages`.
      if (!b.solid) continue;
      // Only the outer ring collides. A courtyard you can't reach without driving through
      // the building first is not worth the extra segments.
      this.addRing(b.poly, true);
    }
  }

  private addRing(poly: number[], outerCcw: boolean): void {
    const n = poly.length / 2;
    if (n < 3) return;
    const ring = this.rings.length;
    const mine: number[] = [];
    this.rings.push(poly);
    this.ringWalls.push(mine);

    for (let i = 0; i < n; i++) {
      const j = (i + 1) % n;
      const ax = poly[i * 2];
      const az = poly[i * 2 + 1];
      const bx = poly[j * 2];
      const bz = poly[j * 2 + 1];
      const dx = bx - ax;
      const dz = bz - az;
      const len = Math.hypot(dx, dz);
      if (len < 1e-4) continue;
      // For a positive-area ring the outward normal of edge a->b is (dz, -dx).
      const s = outerCcw ? 1 : -1;
      this.walls.push({
        ax,
        az,
        bx,
        bz,
        nx: (s * dz) / len,
        nz: (-s * dx) / len,
        ring,
      });
      mine.push(this.walls.length - 1);
      insertSegment(this.grid, this.walls.length - 1, ax, az, bx, bz, 0);
    }

    const bb = bboxOfPoly(poly);
    for (let cx = Math.floor(bb.minX / CELL); cx <= Math.floor(bb.maxX / CELL); cx++) {
      for (let cz = Math.floor(bb.minZ / CELL); cz <= Math.floor(bb.maxZ / CELL); cz++) {
        push(this.ringGrid, cellKey(cx, cz), ring);
      }
    }
  }

  get wallCount(): number {
    return this.walls.length;
  }

  /**
   * Resolves a circle against the buildings around it.
   *
   * A centre that has ended up inside a footprint is pushed straight out along the nearest
   * wall's outward normal, which is what stops a car that clips a corner at speed from
   * being flung through the building instead of out of it. A centre outside is pushed by
   * the walls it actually overlaps — at a convex corner that is two of them, and averaging
   * their push-out is what keeps the car sliding along the corner rather than snagging.
   */
  resolveCircle(x: number, z: number, radius: number): Hit | null {
    const containing = this.ringContaining(x, z);
    if (containing >= 0) {
      let best: Wall | null = null;
      let bestD2 = Infinity;
      for (const wi of this.ringWalls[containing]) {
        const w = this.walls[wi];
        const { d2 } = distToSegment2(x, z, w.ax, w.az, w.bx, w.bz);
        if (d2 < bestD2) {
          bestD2 = d2;
          best = w;
        }
      }
      if (!best) return null;
      const depth = radius + Math.sqrt(bestD2);
      return { px: best.nx * depth, pz: best.nz * depth, nx: best.nx, nz: best.nz, depth };
    }

    const minCx = Math.floor((x - radius) / CELL);
    const maxCx = Math.floor((x + radius) / CELL);
    const minCz = Math.floor((z - radius) / CELL);
    const maxCz = Math.floor((z + radius) / CELL);

    let px = 0;
    let pz = 0;
    let nx = 0;
    let nz = 0;
    let depth = 0;
    let hits = 0;
    const seen = new Set<number>();

    for (let cx = minCx; cx <= maxCx; cx++) {
      for (let cz = minCz; cz <= maxCz; cz++) {
        const bucket = this.grid.get(cellKey(cx, cz));
        if (!bucket) continue;
        for (const wi of bucket) {
          if (seen.has(wi)) continue;
          seen.add(wi);
          const w = this.walls[wi];
          const hit = distToSegment2(x, z, w.ax, w.az, w.bx, w.bz);
          const dist = Math.sqrt(hit.d2);
          if (dist >= radius) continue;

          let dirX: number;
          let dirZ: number;
          if (dist > 1e-5) {
            dirX = (x - hit.x) / dist;
            dirZ = (z - hit.z) / dist;
          } else {
            dirX = w.nx;
            dirZ = w.nz;
          }
          const pen = radius - dist;
          px += dirX * pen;
          pz += dirZ * pen;
          nx += w.nx;
          nz += w.nz;
          if (pen > depth) depth = pen;
          hits++;
        }
      }
    }

    if (!hits) return null;
    const nLen = Math.hypot(nx, nz);
    if (nLen < 1e-6) {
      const pLen = Math.hypot(px, pz);
      if (pLen < 1e-6) return null;
      return { px, pz, nx: px / pLen, nz: pz / pLen, depth };
    }
    return { px: px / hits, pz: pz / hits, nx: nx / nLen, nz: nz / nLen, depth };
  }

  /**
   * How far along the segment from a to b, as a fraction, before it first passes through a
   * wall; 1 when it never does.
   *
   * For the chase camera, which hangs nine metres behind the car on a line that, in a narrow
   * street, runs straight through the corner the car has just turned.
   */
  firstWallAlong(ax: number, az: number, bx: number, bz: number): number {
    const dx = bx - ax;
    const dz = bz - az;
    let first = 1;
    const seen = new Set<number>();
    for (let cx = Math.floor(Math.min(ax, bx) / CELL); cx <= Math.floor(Math.max(ax, bx) / CELL); cx++) {
      for (let cz = Math.floor(Math.min(az, bz) / CELL); cz <= Math.floor(Math.max(az, bz) / CELL); cz++) {
        for (const wi of this.grid.get(cellKey(cx, cz)) ?? []) {
          if (seen.has(wi)) continue;
          seen.add(wi);
          const w = this.walls[wi];
          const ex = w.bx - w.ax;
          const ez = w.bz - w.az;
          const denom = dx * ez - dz * ex;
          if (Math.abs(denom) < 1e-9) continue;
          const t = ((w.ax - ax) * ez - (w.az - az) * ex) / denom;
          const u = ((w.ax - ax) * dz - (w.az - az) * dx) / denom;
          if (t >= 0 && t < first && u >= 0 && u <= 1) first = t;
        }
      }
    }
    return first;
  }

  /** Index of the first footprint containing the point, or -1. */
  private ringContaining(x: number, z: number): number {
    const bucket = this.ringGrid.get(cellKey(Math.floor(x / CELL), Math.floor(z / CELL)));
    if (!bucket) return -1;
    for (const ri of bucket) if (pointInPoly(this.rings[ri], x, z)) return ri;
    return -1;
  }
}

// ---------------------------------------------------------------------------
// grid insertion
// ---------------------------------------------------------------------------

function push(grid: Map<number, number[]>, key: number, index: number): void {
  const bucket = grid.get(key);
  if (bucket) bucket.push(index);
  else grid.set(key, [index]);
}

function insertSegment(
  grid: Map<number, number[]>,
  index: number,
  ax: number,
  az: number,
  bx: number,
  bz: number,
  pad: number,
): void {
  const minCx = Math.floor((Math.min(ax, bx) - pad) / CELL);
  const maxCx = Math.floor((Math.max(ax, bx) + pad) / CELL);
  const minCz = Math.floor((Math.min(az, bz) - pad) / CELL);
  const maxCz = Math.floor((Math.max(az, bz) + pad) / CELL);
  for (let cx = minCx; cx <= maxCx; cx++) {
    for (let cz = minCz; cz <= maxCz; cz++) push(grid, cellKey(cx, cz), index);
  }
}

function insertCircle(
  grid: Map<number, number[]>,
  index: number,
  x: number,
  z: number,
  r: number,
): void {
  const minCx = Math.floor((x - r) / CELL);
  const maxCx = Math.floor((x + r) / CELL);
  const minCz = Math.floor((z - r) / CELL);
  const maxCz = Math.floor((z + r) / CELL);
  for (let cx = minCx; cx <= maxCx; cx++) {
    for (let cz = minCz; cz <= maxCz; cz++) push(grid, cellKey(cx, cz), index);
  }
}
